import { Table } from 'antd';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import UpdateDunningInfoModal from '../../../components/UpdateDunningInfoModal';
import { numberWithCommas } from '../../../helpers/number';
import {
  getBPListingThunk,
  getPTPListingThunk,
} from '../../../services/collectionService/collectionThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDate, displayDateAndTime } from '../../../utils/datetime.util';
import AccountEnquiryModal from '../../AccountEnquiry/AccountEnquiryModal';

import type { TableProps } from 'antd/es/table';
import type { AccountDetails } from '../../../features/collection/collectionSlice';
import { ActionType } from '../../../enum/actionType';

const BPListing = ({ callback }: { callback?: () => void }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const { bpList } = useSelector((state: RootState) => state.collection);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<AccountDetails>>({});

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: pageNum ? +pageNum : 1,
        pageSize: pageSize ? +pageSize : 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    [pageNum, pageSize]
  );

  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const tablePagination = {
    total: bpList.data?.fixTotal,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const handleTableChange: TableProps<AccountDetails>['onChange'] = async (
    pagination,
    filters,
    sorter: any,
    extra
  ) => {
    const current = extra?.action === 'sort' ? 1 : pagination.current ?? 1;
    const pageSize = pagination.pageSize ?? 10;
    const effectivePagination = { ...pagination, current, pageSize };

    const param =
      sorter?.order == null
        ? {
            ...initialParams,
            ...filterState,
            pagination: effectivePagination,
          }
        : {
            ...initialParams,
            ...filterState,
            pagination: effectivePagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
          };

    dispatch(getBPListingThunk(param));
    callback?.();
    setPaginationState({
      current,
      pageSize,
    });
    setSortedInfo(sorter as SorterResult<AccountDetails>);

    queryParams.set('pageNum', `${current}`);
    queryParams.set('pageSize', `${pageSize}`);
    const newSearch = queryParams.toString();
    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  const updateCompleteCallback = () => {
    dispatch(getBPListingThunk(initialParams));
    dispatch(getPTPListingThunk(initialParams));
    callback?.();
  };

  const columns: ColumnsType<any> = [
    {
      title: 'No',
      key: 'index',
      dataIndex: 'index',
      fixed: 'left',
      render: (_, __, index: number) =>
        (paginationState.current - 1) * paginationState.pageSize + index + 1,
    },
    {
      title: 'DPD',
      dataIndex: 'dpd',
      key: 'dpd',
      fixed: 'left',
      sorter: (a, b) => a.accountDetails.dpd - b.accountDetails.dpd,
      sortOrder: sortedInfo.columnKey === 'dpd' ? sortedInfo.order : null,
      render: (text, record) => (record.accountDetails.dpd ? record.accountDetails.dpd : '-'),
    },
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      sorter: (a, b) => a.accountDetails.agreementNo - b.accountDetails.agreementNo,
      sortOrder: sortedInfo.columnKey === 'agreementNo' ? sortedInfo.order : null,
      fixed: 'left',
      render: (_, record) => (
        <AccountEnquiryModal key={record?.id} agreementNo={record.accountDetails?.agreementNo} />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'applicantFirstName',
      key: 'applicantFirstName',
      sorter: (a, b) => a.accountDetails.applicantFirstName - b.accountDetails.applicantFirstName,
      sortOrder: sortedInfo.columnKey === 'applicantFirstName' ? sortedInfo.order : null,
      fixed: 'left',
      width: '200px',
      render: (text, record) => record?.accountDetails.applicantFirstName ?? '-',
    },
    {
      title: 'i-score',
      dataIndex: ['accountDetails', 'iScoreValue'],
      key: 'iScoreValue',
      sorter: (a, b) => a.accountDetails.iScoreValue - b.accountDetails.iScoreValue,
      sortOrder: sortedInfo.columnKey === 'iScoreValue' ? sortedInfo.order : null,
      fixed: 'left',
      render: (text, record) => text ?? '-',
    },
    {
      title: 'Total Due Outstanding',
      dataIndex: ['accountDetails', 'totalDueInstalment'],
      key: 'totalDueInstalment',
      sorter: (a, b) => a.accountDetails.totalDueInstalment - b.accountDetails.totalDueInstalment,
      sortOrder: sortedInfo.columnKey === 'totalDueInstalment' ? sortedInfo.order : null,
      fixed: 'left',
      render: (_, record) =>
        record.accountDetails?.totalDueInstalment
          ? `RM ${numberWithCommas(
              record.accountDetails?.totalDueInstalment > 0
                ? record.accountDetails?.totalDueInstalment
                : 0
            )}`
          : '-',
    },
    {
      title: 'Total Billing Outstanding',
      dataIndex: ['accountDetails', 'billingOutstanding'],
      key: 'billingOutstanding',
      sorter: (a, b) => a.accountDetails.billingOutstanding - b.accountDetails.billingOutstanding,
      sortOrder: sortedInfo.columnKey === 'billingOutstanding' ? sortedInfo.order : null,
      fixed: 'left',
      render: (_, record) =>
        record.accountDetails?.billingOutstanding
          ? `RM ${numberWithCommas(
              record.accountDetails?.billingOutstanding > 0
                ? record.accountDetails?.billingOutstanding
                : 0
            )}`
          : '-',
    },
    {
      title: 'Date & Time',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: (a, b) => a.dpd - b.dpd,
      sortOrder: sortedInfo.columnKey === 'updatedAt' ? sortedInfo.order : null,
      fixed: 'left',
      width: '200px',
      render: (_, record) => displayDateAndTime(record.updatedAt),
    },
    {
      title: 'User ID',
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: (a, b) => a.dpd - b.dpd,
      sortOrder: sortedInfo.columnKey === 'createdBy' ? sortedInfo.order : null,
      fixed: 'left',
      width: '200px',
      render: (_, record) => record.createdBy,
    },
    {
      title: 'PTP Date',
      dataIndex: 'ptpDate',
      key: 'ptpDate',
      sorter: (a, b) => a.dpd - b.dpd,
      sortOrder: sortedInfo.columnKey === 'ptpDate' ? sortedInfo.order : null,
      fixed: 'left',
      width: '200px',
      render: (_, record) => (record ? displayDate(record.ptpDate) : '-'),
    },
    {
      title: 'PTP Amount',
      dataIndex: 'ptpAmount',
      key: 'ptpAmount',
      fixed: 'left',
      width: 50,
      render: (_, record) =>
        record.ptpAmount != null ? `RM` + numberWithCommas(record.ptpAmount) : '-',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      fixed: 'left',
      width: '200px',
      render: (_, record) => (
        <UpdateDunningInfoModal
          key={`"updateDunningInfoModal${record?.id}`}
          accountDetailsId={record?.accountDetailsId}
          agreementNo={record?.accountDetails.agreementNo}
          onFinishCallback={updateCompleteCallback}
          isUpdate={true}
          id={record?.id}
          statusActivity={ActionType.BP}
          prevPtpAmount={record?.ptpAmount ?? 0}
        />
      ),
    },
  ];

  useEffect(() => {
    dispatch(getBPListingThunk(initialParams));
  }, []);

  return (
    <>
      <Helmet>
        <title>BP - redCASH CEP</title>
      </Helmet>
      <Table
        rowKey="id"
        columns={columns}
        loading={bpList.isLoading}
        dataSource={bpList.data?.data}
        pagination={tablePagination}
        onChange={handleTableChange}
      />
    </>
  );
};
export default BPListing;
